/**
 *  @swagger
 *  definitions:
 *    ChampionshipParams:
 *      type: object
 *      properties:
 *        RegionSeasonId:
 *          description: ID of the associated RegionSeason
 *          type: string
 *        ReferenceId:
 *          description: ID of the associated Championship
 *          type: string
 *        Lock:
 *          description: Lock of the Regatta
 *          type: boolean
 *        Public:
 *          description: Public of the Championship
 *          type: boolean
 *        Name:
 *          description: Name of the Championship
 *          type: string
 *        Slug:
 *          description: Slug of the Championship
 *          type: string
 *        Content:
 *          description: Content of the Championship
 *          type: string
 *        Image:
 *          description: Image of the Championship
 *          type: string
 *        OrganizerName:
 *          description: Organizer Name of the Championship
 *          type: string
 *        OrganizerEmail:
 *          description: Organizer Email of the Championship
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    ChampionshipCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipParams'
 *        - type: object
 *          required:
 *            - RegionSeasonId
 *            - Lock
 *            - Public
 *            - Name
 *            - OrganizerEmail
 *
 *  @swagger
 *  definitions:
 *    ChampionshipQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Championship
 *              type: string
 *        - $ref: '#/definitions/ChampionshipParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Championship:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipCreateParams'
 *        - $ref: '#/definitions/ChampionshipUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Championship:
 *        allOf:
 *          - $ref: '#/definitions/Championship'
 *          - type: object
 *            properties:
 *              RegionSeason:
 *                description: RegionSeason of the Championship
 *                $ref: '#/components/schemas/RegionSeason'
 *              Reference:
 *                description: Reference of the Championship
 *                $ref: '#/components/schemas/Championship'
 *              ChampionshipResultTables:
 *                description: ChampionshipResultTables of the Championship
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipResultTable'
 *              ChampionshipDocuments:
 *                description: ChampionshipDocuments of the Championship
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipDocument'
 *              ChampionshipAdminRequests:
 *                description: ChampionshipAdminRequests of the Championship
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipAdminRequest'
 *              Regattas:
 *                description: Regattas of the Championship
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Regatta'
 *              Admins:
 *                description: Admins of the Championship
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Admin'
 *              ChampionshipSubscriptions:
 *                description: ChampionshipSubscriptions of the Championship
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipSubscription'
 *              ChampionshipSponsors:
 *                description: ChampionshipSponsors of the Championship
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipSponsor'
 */

// Helpers
import {
  validateSlug,
} from '../helpers';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ReferenceId: null | string;
  RegionSeasonId: string;
};

export type $Params = {
  Content: null | string;
  Image: null | string;
  Lock: boolean;
  Name: string;
  OrganizerEmail: string;
  OrganizerName: null | string;
  Public: boolean;
  Slug: null | string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Admins?: Array<$Model<'Admin'>>;
  ChampionshipAdminRequests?: Array<$Model<'ChampionshipAdminRequest'>>;
  ChampionshipDocuments?: Array<$Model<'ChampionshipDocument'>>;
  ChampionshipResultTables?: Array<$Model<'ChampionshipResultTable'>>;
  ChampionshipSponsors?: Array<$Model<'ChampionshipSponsor'>>;
  ChampionshipSubscriptions?: Array<$Model<'ChampionshipSubscription'>>;
  Reference?: $Model<'Championship'>;
  Regattas?: Array<$Model<'Regatta'>>;
  RegionSeason?: $Model<'RegionSeason'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Lock: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  OrganizerEmail: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  OrganizerName: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Slug: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Championship'> => {
  const Championship = stack.define(
    'Championship',
    fields(Sequelize),
    {
      tableName: 'Championships',
    },
  );

  Championship.associate = (models: $ModelClasses) => {
    models.Championship.belongsTo(
      models.RegionSeason,
      {
        as: 'RegionSeason',
        foreignKey: 'RegionSeasonId',
      },
    );
    models.Championship.belongsTo(
      models.Championship,
      {
        as: 'Reference',
        constraints: false,
        foreignKey: 'ReferenceId',
      },
    );
    models.Championship.hasMany(
      models.ChampionshipResultTable,
      {
        as: 'ChampionshipResultTables',
        foreignKey: 'ChampionshipId',
      },
    );
    models.Championship.hasMany(
      models.ChampionshipDocument,
      {
        as: 'ChampionshipDocuments',
        foreignKey: 'ChampionshipId',
      },
    );
    models.Championship.hasMany(
      models.ChampionshipAdminRequest,
      {
        as: 'ChampionshipAdminRequests',
        foreignKey: 'ChampionshipId',
      },
    );
    models.Championship.hasMany(
      models.ChampionshipSponsor,
      {
        as: 'ChampionshipSponsors',
        foreignKey: 'ChampionshipId',
      },
    );
    models.Championship.hasMany(
      models.ChampionshipSubscription,
      {
        as: 'ChampionshipSubscriptions',
        foreignKey: 'ChampionshipId',
      },
    );
    models.Championship.belongsToMany(
      models.Regatta,
      {
        as: 'Regattas',
        foreignKey: 'ChampionshipId',
        otherKey: 'RegattaId',
        through: models.ChampionshipRegatta,
      },
    );
    models.Championship.belongsToMany(
      models.Admin,
      {
        as: 'Admins',
        foreignKey: 'ChampionshipId',
        otherKey: 'AdminId',
        through: models.ChampionshipAdmin,
      },
    );
  };

  Championship.hooks = (models: $ModelClasses) => {
    Championship.addHook(
      'beforeValidate',
      async (championship: $Model<'Championship'>) => {
        if (typeof championship.Slug !== 'undefined') {
          let currentValue: null | string = null;

          if (championship.id) {
            currentValue = await Championship.findByPk(championship.id).Slug;
          }

          /* eslint-disable-next-line no-param-reassign,require-atomic-updates */
          championship.Slug = await validateSlug({
            currentValue,
            models,
            newValue: championship.Slug,
            RegionSeasonId: championship.RegionSeasonId,
          });
        }
      },
    );
  };

  Championship.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return Championship;
});
